import { CSRF } from '../utils/CSRF'
import { BaseRepository } from './BaseRepository'

interface User {
  id: number
  email: string
  name?: string | null
  can_view_profile: boolean
  can_manage_billing: boolean
  can_manage_api: boolean
  has_simulations_access: boolean
  has_simulations_nav_access: boolean
  has_identity_verifications_access: boolean
  has_meetings_access: boolean
  has_outbounding_agents_access: boolean
  is_admin_user: boolean
  has_signal_access: boolean
  has_guard_access: boolean
  product_access?: ProductAccessMatrix
  current_team_id: string
  current_team_name: string
  pinned_sidebar_items?: string[]
  server?: string
  stream_server?: string
}

interface Team {
  id: string
  uuid: string
  name: string
  plan: string
  supported_languages: string[]
}

interface SubscriptionProductUsage {
  consumed: number
  allocated: number
  available: number
  is_unlimited?: boolean
}

interface SubscriptionProducts {
  voice_design?: SubscriptionProductUsage
  rapid_voice_clone?: SubscriptionProductUsage
  pro_voice_clone?: SubscriptionProductUsage
  team_seats?: SubscriptionProductUsage
  projects?: SubscriptionProductUsage
}

interface Stats {
  voices_created: number
  credits_available: number
  characters_used: number
  team_members: number
  total_seats: number
  billing_model: 'per_character' | 'per_second' | 'unknown' | 'wallet'
  is_payg: boolean
  uses_billing_engine?: boolean
  uses_measure?: boolean
  subscription_products?: SubscriptionProducts
}

export interface TeamMember {
  id: number
  name: string
  email: string
  is_admin: boolean
}

export interface TeamData {
  team: Team
  stats: Stats
  members: TeamMember[]
}

export interface UserData {
  user: User
  teams: Team[]
  studio_url: string
}

let hasWarnedMissingUserData = false

declare global {
  interface Window {
    CURRENT_USER_EMAIL: string
    CURRENT_USER_FIRST_NAME: string
    CURRENT_USER_TEAM_ID: string
    CURRENT_USER_TEAM_NAME: string
    CURRENT_USER_TEAM_UUID: string
    CURRENT_USER_LAST_LOGIN_AT: string
    CURRENT_USER_TEAMS: Array<{ id: string; uuid: string; name: string }>
    CURRENT_USER_PINNED_SIDEBAR_ITEMS?: string[]
    CAN_VIEW_PROFILE: boolean
    CAN_MANAGE_BILLING: boolean
    CAN_MANAGE_API: boolean
    HAS_SIMULATIONS_ACCESS?: boolean
    HAS_SIMULATIONS_NAV_ACCESS?: boolean
    HAS_IDENTITY_VERIFICATIONS_ACCESS?: boolean
    HAS_MEETINGS_ACCESS?: boolean
    HAS_OUTBOUNDING_AGENTS_ACCESS?: boolean
    IS_ADMIN_USER?: boolean
    HAS_SIGNAL_ACCESS?: boolean
    HAS_GUARD_ACCESS?: boolean
    PRODUCT_ACCESS?: ProductAccessMatrix
    STUDIO_URL?: string
  }
}

export const updateWindowUserData = (userData?: UserData | null) => {
  if (!userData?.user) {
    if (!hasWarnedMissingUserData) {
      console.warn('updateWindowUserData: received empty user payload, skipping window state update.')
      hasWarnedMissingUserData = true
    }
    return
  }
  hasWarnedMissingUserData = false

  window.CURRENT_USER_EMAIL = userData.user.email
  window.CURRENT_USER_TEAM_ID = userData.user.current_team_id
  window.CURRENT_USER_TEAM_NAME = userData.user.current_team_name
  window.CURRENT_USER_TEAMS = userData.teams ?? []
  window.CURRENT_USER_PINNED_SIDEBAR_ITEMS = userData.user.pinned_sidebar_items ?? []
  window.CAN_VIEW_PROFILE = userData.user.can_view_profile
  window.CAN_MANAGE_BILLING = userData.user.can_manage_billing
  window.CAN_MANAGE_API = userData.user.can_manage_api
  window.HAS_SIMULATIONS_ACCESS = userData.user.has_simulations_access
  window.HAS_SIMULATIONS_NAV_ACCESS = userData.user.has_simulations_nav_access
  window.HAS_OUTBOUNDING_AGENTS_ACCESS = userData.user.has_outbounding_agents_access
  window.IS_ADMIN_USER = userData.user.is_admin_user
  window.PRODUCT_ACCESS = userData.user.product_access ?? window.PRODUCT_ACCESS
  window.HAS_IDENTITY_VERIFICATIONS_ACCESS = userData.user.has_identity_verifications_access === true
  window.HAS_MEETINGS_ACCESS = window.PRODUCT_ACCESS?.meetings?.visible ?? userData.user.has_meetings_access
  window.HAS_SIGNAL_ACCESS = window.PRODUCT_ACCESS?.signal?.visible ?? userData.user.has_signal_access
  window.HAS_GUARD_ACCESS = window.PRODUCT_ACCESS?.trace?.visible ?? userData.user.has_guard_access
  window.STUDIO_URL = userData.studio_url
}

export class TeamsRepository extends BaseRepository {
  getCurrentUser() {
    return super.getHttpClientInstance().get<UserData>('/api/spa/v1/team/user.json', {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }
  getTeam() {
    return super.getHttpClientInstance().get<TeamData>('/api/spa/v1/team.json', {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }

  addMember(email: string) {
    return super.getHttpClientInstance().post(
      '/api/spa/v1/team/members.json',
      { email },
      {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      },
    )
  }

  removeMember(memberId: number) {
    return super.getHttpClientInstance().delete(`/api/spa/v1/team/members/${memberId}.json`, {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }

  updateAdmin(memberId: number, isAdmin: boolean) {
    return super.getHttpClientInstance().put(
      `/api/spa/v1/team/members/${memberId}/update_admin.json`,
      { is_admin: isAdmin },
      {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      },
    )
  }

  updateTeam(name: string) {
    return super.getHttpClientInstance().patch(
      '/api/spa/v1/team.json',
      { team: { name } },
      {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      },
    )
  }

  switchTeam(teamUuid: string) {
    return super.getHttpClientInstance().post(
      `/api/spa/v1/team/switch/${teamUuid}.json`,
      {},
      {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      },
    )
  }

  getPinnedItems() {
    return super.getHttpClientInstance().get<{ pinned: string[] }>('/api/spa/v1/team/pinned_items.json', {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }

  pinItem(path: string) {
    return super.getHttpClientInstance().post<{ pinned: string[] }>(
      '/api/spa/v1/team/pinned_items.json',
      { path },
      {
        'X-CSRF-Token': CSRF.getToken(),
        'Content-Type': 'application/json',
      },
    )
  }

  unpinItem(path: string) {
    return super.getHttpClientInstance().delete<{ pinned: string[] }>(`/api/spa/v1/team/pinned_items.json?path=${encodeURIComponent(path)}`, {
      'X-CSRF-Token': CSRF.getToken(),
      'Content-Type': 'application/json',
    })
  }
}
